import {ChangeDetectorRef, Component, EventEmitter, inject, OnDestroy, OnInit, Output} from '@angular/core';
import {Document, DocumentValidation, GetParams, KeyValueGroup, Partner, User} from '@app/shared';
import {NZ_DRAWER_DATA, NzDrawerRef, NzDrawerService} from 'ng-zorro-antd/drawer';
import {NzModalService} from 'ng-zorro-antd/modal';
import {DocModalComponent} from '../doc-modal/doc-modal.component';
import {combineLatest, take} from 'rxjs';
import {CommunicationService, DocumentService, DrawerEscapeBlockerService, isExpirationAlert, ScreenService} from '@app/core';
import {AuthService} from '../../../core/services/auth.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ProjectUtilsService} from '../../../features/project/services/project-utils.service';
import {GlobalValuesService} from '../../../core/services/global-values.service';

@Component({
  selector: 'app-doc-drawer',
  standalone: false,

  templateUrl: './doc-drawer.component.html',
  styleUrl: './doc-drawer.component.less',
})
export class DocDrawerComponent implements OnInit, OnDestroy {
  nzData: { document: Document; showOptions: true } = inject(NZ_DRAWER_DATA);

  pdfSrc = 'http://localhost:4200/assets/documents/sample.pdf';

  docsModalRef: any;
  uploadDocsDrawerRef: any;

  @Output() deleteEvent: EventEmitter<Document[]> = new EventEmitter<
    Document[]
  >();
  @Output() statusEvent: EventEmitter<void> = new EventEmitter<void>();

  partnerOptions: Partner[] = [];

  isMobile$;

  lastValidationSuccess: DocumentValidation | undefined;
  lastValidationRejected: DocumentValidation | undefined;

  loggedUser: User | undefined;

  constructor(
    private drawerRef: NzDrawerRef<string>,
    private drawerService: NzDrawerService,
    private modalService: NzModalService,
    private cdr: ChangeDetectorRef,
    private screenService: ScreenService,
    private ps: ProjectUtilsService,
    private documentService: DocumentService,
    private communicationService: CommunicationService,
    public authService: AuthService,
    public gs: GlobalValuesService,
    private drawerEscapeBlocker: DrawerEscapeBlockerService,
  ) {
    this.partnerOptions = this.gs.partnerlist;
    this.isMobile$ = screenService.isMobile$;
    authService.loggedUser
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.loggedUser = value));
  }

  ngOnInit(): void {
    this.getLastlyDocumentValidation();
    this.getChat();

    // Registrar handler nativo en fase de captura para interceptar Escape
    this.drawerEscapeBlocker.block();
  }

  getLastlyDocumentValidation(): void {
    if (
      this.nzData.document.documentValidations &&
      this.nzData.document.documentValidations.length > 0
    ) {
      const withReasonIds = this.nzData.document.documentValidations.filter(
        (o) => !o.reason,
      );
      const withoutReasonIds = this.nzData.document.documentValidations.filter(
        (o) => !!o.reason,
      );

      this.lastValidationSuccess = withReasonIds[withReasonIds.length - 1];
      this.lastValidationRejected =
        withoutReasonIds[withoutReasonIds.length - 1];
    }
  }

  close(): void {
    this.drawerRef.close(this.nzData);
  }

  setKeyValueData(type: number): KeyValueGroup[] {
    const expData = isExpirationAlert(this.nzData.document.expirationDate);
    if (type === 1) {
      return [
        {
          layout: 'normal',
          name: 'normal',
          items: [
            {
              label: 'Google Drive',
              parentObject: this.nzData.document,
              component: 'DocDrawerComponent',
              noWrap: false,
              editable: false,
              required: false,
              type: 'btn',
              path: 'name',
              value: {
                label: this.nzData.document.name,
                localPrefix: 'google_drive-icon.svg',
                sufix: 'open_in_new',
                link: this.nzData.document.url,
              },
            },
            {
              parentObject: this.nzData.document,
              component: 'DocDrawerComponent',
              list: this.partnerOptions,
              label: 'Subido por',
              noWrap: false,
              editable: true,
              required: false,
              type: 'avatar',
              path: 'ownerId',
              value: {
                userId: this.nzData.document.ownerId,
                size: 24,
                centered: true,
              },
            },
            {
              label: 'Tamaño',
              parentObject: this.nzData.document,
              component: 'DocDrawerComponent',
              noWrap: false,
              editable: false,
              required: false,
              type: 'text',
              path: 'fileSize',
              value: this.nzData.document.fileSize,
            },
            {
              label:
                expData.alertState === 'expired' ? 'Expirado el' : 'Expira el',
              parentObject: this.nzData.document,
              component: 'DocDrawerComponent',
              noWrap: false,
              required: false,
              editable: true,
              type: 'date',
              path: 'expirationDate',
              value: {
                startDate: this.nzData.document.expirationDate,
                format: 'd MMM yyyy - hh:mm a',
                showHours: true,
                showExpirationAlert: true,
              },
            },
          ],
        },
      ];
    } else {
      const keyValueData: KeyValueGroup[] = [
        {
          layout: 'normal',
          name: 'normal',
          items: [],
        },
      ];

      if (this.nzData.document.validationStatus !== 'ValidationPending') {
        keyValueData[0].items.push({
          label: 'Validador',
          parentObject: this.nzData.document,
          component: 'DocDrawerComponent',
          list: this.partnerOptions,
          noWrap: false,
          required: false,
          editable: true,
          type: 'avatar',
          path: 'partnerId',
          value: {
            userId: this.nzData.document.partnerId,
            size: 24,
            centered: true,
          },
        });
      }

      if (this.lastValidationSuccess?.createdOn) {
        keyValueData[0].items.push({
          label: 'Fecha Validación',
          parentObject: this.lastValidationSuccess,
          component: 'DocDrawerComponent',
          noWrap: false,
          required: false,
          editable: true,
          type: 'date',
          path: 'createdOn',
          value: {
            startDate: this.lastValidationSuccess?.createdOn,
            format: 'd MMM yyyy - hh:mm a',
          },
        });
      }

      if (this.lastValidationRejected?.createdOn) {
        keyValueData[0].items.push({
          label: 'Fecha Corrección',
          parentObject: this.lastValidationRejected,
          component: 'DocDrawerComponent',
          noWrap: false,
          required: false,
          editable: true,
          type: 'date',
          path: 'createdOn',
          value: {
            startDate: this.lastValidationRejected?.createdOn,
            format: 'd MMM yyyy - hh:mm a',
          },
        });
      }

      if (
        this.lastValidationRejected?.reason &&
        this.nzData.document.validationStatus !== 'Validated'
      ) {
        keyValueData[0].items.push({
          label: 'Motivo de rechazo',
          parentObject: this.lastValidationRejected,
          component: 'DocDrawerComponent',
          noWrap: false,
          required: false,
          editable: true,
          path: 'reason',
          type: 'text',
          value: this.lastValidationRejected?.reason,
        });
      }

      // if (this.nzData.document.rejectionDate) {
      //   keyValueData[0].items.push({
      //     label: 'Fecha Rechazo',
      //     parentObject: this.nzData.document,
      //     component: 'DocDrawerComponent',
      //     noWrap: false,
      //     required: false,
      //     editable: true,
      //     type: 'date',
      //     path: 'rejectionDate',
      //     value: {
      //       startDate: this.nzData.document.rejectionDate,
      //       format: 'd MMM - hh:mm a',
      //     }
      //   })
      // }

      return keyValueData;
    }
  }

  // Esto simula una peticion http para buscar el chat correspondiente
  getChat() {
    if (this.nzData.document.chatId) {
      const chatParams: GetParams = {
        startRow: 0,
        endRow: 1,
        sortProperty: 'id',
        sortDirection: 'asc',
        filterProperty: 'id',
        filterValue: this.nzData.document.chatId.toString(),
      };
      this.communicationService.getChat(chatParams).subscribe({
        next: (rsp) => {
          this.nzData.document.chat = rsp?.data?.rows[0];
          if (
            this.nzData.document.chat &&
            !this.nzData.document.chat.chatMessages
          ) {
            this.nzData.document.chat['chatMessages'] = [];
          }
          this.cdr.detectChanges();
        },
        error: (e) => console.error(e),
      });
    }
  }

  openDocModal(
    type: 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete',
  ): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        if (isMobile && (type === 'upload' || type === 'correct')) {
          this.uploadDocsDrawerRef = this.drawerService.create({
            nzClosable: false,
            nzMask: false,
            nzHeight: '98%',
            nzPlacement: 'bottom',
            nzContent: DocModalComponent,
            nzData: {
              docModalType: type,
              document: this.nzData.document,
            },
          });
        } else {
          this.docsModalRef = this.modalService.create({
            nzFooter: null,
            nzContent: DocModalComponent,
            nzClosable: false,
            nzAutofocus: null,
            nzCentered: true,
            nzWidth: 'auto',
          });

          const modal = this.docsModalRef.getContentComponent();
          modal.docModalType = type;
          modal.document = this.nzData.document;
          modal.documentsToDelete =
            type === 'delete' ? [this.nzData.document] : undefined;
          modal.deleteEvent.pipe(take(1)).subscribe((docs: Document[]) => {
            this.deleteEvent.emit(docs);
            this.close();
          });

          // Para q se actualicen los cambios
          this.docsModalRef.afterClose.pipe(take(1)).subscribe(() => {
            this.getLastlyDocumentValidation();
            this.statusEvent.emit();
            this.cdr.detectChanges();
          });
        }
      });
  }

  downloadDocument(): void {
    this.documentService.openDocumentInNewTab(this.nzData.document);
  }

  ngOnDestroy(): void {
    // Remover listener nativo si fue registrado
    this.drawerEscapeBlocker.unblock();
  }
}
